import React, { useEffect, useState, useMemo } from "react"; // PERF‑ADDED
import {
  Box,
  Flex,
  SimpleGrid,
  Button,
  Input,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Select,
  useToast,
  Switch,
  Text,
  IconButton,
  Spinner,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import { cqAPI } from "../../lib/Auth";
import EditableText from "../EditableText/EditableText";
import {
  adminPriceLabel,
  adminFixedPriceOf,
  describeLicensedTiers,
  isLicensedPrice,
  isTieredPrice,
} from "./priceFormat";

function Bundles() {
  const [bundles, setBundles] = useState([]);
  const [products, setProducts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [newBundle, setNewBundle] = useState({
    name: "",
    description: "",
    items: [],
    partner_id: "",
    overage_fee_share: "", // ADDED
  });

  const { isOpen, onOpen, onClose } = useDisclosure({
    onClose: () =>
      setNewBundle({
        name: "",
        description: "",
        items: [],
        partner_id: "",
        overage_fee_share: "", // ADDED
      }),
  });
  const toast = useToast();
  const [isSavingBundle, setIsSavingBundle] = useState(false);

  const fetchBundles = async () => {
    const response = await cqAPI.get("/settings/subscriptions/bundles");
    setBundles(response?.data?.bundles ?? []);
  };

  const fetchProducts = async () => {
    const response = await cqAPI.get("/settings/subscriptions");
    setProducts(response?.data?.availableProducts ?? []);
  };

  const fetchPartners = async () => {
    const response = await cqAPI.get("/settings/partners");
    if (response?.type === "ok") {
      setPartners(response.data.partners || []);
    } else {
      toast({
        title: "Error",
        description: "Failed to load partners.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchBundles();
    fetchProducts();
    fetchPartners();
  }, []);

  const handleSaveBundle = async () => {
    if (!newBundle.name || !newBundle.description) {
      toast({
        title: "Validation Error",
        description: "Name and description cannot be empty.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    if (newBundle.items.length === 0) {
      toast({
        title: "Validation Error",
        description: "At least one product must be added.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    for (const item of newBundle.items) {
      if (!item.product || !item.price) {
        toast({
          title: "Validation Error",
          description: "All products must have a selected price.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
    }

    // ADDED ➜ validate overage_fee_share when partner chosen
    if (
      newBundle.partner_id &&
      (newBundle.overage_fee_share === "" ||
        isNaN(newBundle.overage_fee_share) ||
        Number(newBundle.overage_fee_share) < 0 ||
        Number(newBundle.overage_fee_share) > 100)
    ) {
      toast({
        title: "Validation Error",
        description: "Overage Split % for partner must be 0 – 100.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSavingBundle(true);
      // ADDED ➜ build payload with numeric overage_fee_share when relevant
      const bundleToSend = { ...newBundle };
      if (newBundle.partner_id) {
        bundleToSend.overage_fee_share = Number(newBundle.overage_fee_share);
      } else {
        delete bundleToSend.overage_fee_share;
      }

      const response = await cqAPI.post(
        "/settings/subscriptions/bundles",
        bundleToSend // ADDED
      );
      if (response.type === "error") {
        toast({
          title: "Error",
          description: response.msg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setIsSavingBundle(false);
        return;
      }
      await fetchBundles();
      onClose();
      setNewBundle({
        name: "",
        description: "",
        items: [],
        partner_id: "",
        overage_fee_share: "", // ADDED
      });
      setIsSavingBundle(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while saving the bundle.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setIsSavingBundle(false);
    }
  };

  const handleAddProduct = () => {
    setNewBundle((prevBundle) => ({
      ...prevBundle,
      items: [...prevBundle.items, { product: "", price: "" }],
    }));
  };

  const handleRemoveProduct = (index) => {
    setNewBundle((prevBundle) => ({
      ...prevBundle,
      items: prevBundle.items.filter((_, i) => i !== index),
    }));
  };

  const handleProductChange = (index, field, value) => {
    const updatedItems = newBundle.items.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    setNewBundle((prevBundle) => ({ ...prevBundle, items: updatedItems }));
  };

  const handleSave = async (bundleId, field, newValue) => {
    const response = await cqAPI.put("/settings/subscriptions/bundles", {
      _id: bundleId,
      field,
      value: newValue,
    });
    if (response.type === "error") {
      toast({
        title: "Error",
        description: response.msg,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
    setBundles(
      bundles.map((bundle) => {
        if (bundle._id !== bundleId) {
          return bundle;
        }
        return {
          ...bundle,
          [field]: newValue,
        };
      })
    );
    return true;
  };

  // SS-4462: licensed tiered prices count too, at a quantity of one
  const calculateFixedPrice = (bundle) => {
    let total = 0;
    bundle.items.forEach(([_product, price]) => {
      total += adminFixedPriceOf(price);
    });
    return total;
  };

  /* ---------- PERF: memo‑cache heavy grid ---------- */
  const bundlesGrid = useMemo( // PERF‑ADDED
    () => ( // PERF‑ADDED
      <SimpleGrid columns={{ base: 1, md: 3, lg: 3 }} spacing="20px">
        {bundles.map((bundle, index) => (
          <Box
            key={index}
            borderWidth="1px"
            borderRadius="lg"
            mb={4}
            width="100%"
            className="bundle-container"
          >
            <Box bg="blue.500" color="white" p={4} borderTopRadius="lg">
              <EditableText
                initialValue={bundle.name}
                onSave={(e) => handleSave(bundle._id, "name", e)}
                showPlaceholderInDisplayMode={false}
                placeholder="Name"
                pattern={/^.{1,15}$/}
                invalid="The name can be up to 15 characters and cannot be empty."
              />
            </Box>
            <Box p={4}>
              <Flex justifyContent="space-between" alignItems="left">
                <EditableText
                  initialValue={bundle.description}
                  onSave={(e) => handleSave(bundle._id, "description", e)}
                  showPlaceholderInDisplayMode={false}
                  placeholder="Description"
                  multiline={true}
                  pattern={/^.{1,55}$/}
                  invalid="The description can be up to 55 characters and cannot be empty."
                />
              </Flex>
              <Flex mt={4} alignItems="center" justifyContent="space-between">
                <Text>Private</Text>
                <Switch
                  isChecked={bundle.visible_to_admins_only}
                  onChange={(event) => {
                    handleSave(
                      bundle._id,
                      "visible_to_admins_only",
                      event.target.checked
                    );
                  }}
                  className="bundle-private-slider"
                />
              </Flex>
              <Flex mt={4} alignItems="center" justifyContent="space-between">
                <Text>Active</Text>
                <Switch
                  isChecked={bundle.active}
                  onChange={(event) => {
                    handleSave(bundle._id, "active", event.target.checked);
                  }}
                  className="bundle-active-slider"
                />
              </Flex>
              <Box mt={4}>
                <Text fontWeight="bold">Products and Prices:</Text>
                {bundle.overage_fee_share && (<Text>Partner Share: {bundle.overage_fee_share}%</Text>)}
                {bundle.items.map(([product, price], itemIndex) => (
                  <Box key={itemIndex} mt={2}>
                    <Text>
                      {product.name} -{" "}
                      {adminPriceLabel(product, price)}
                      {price?.metadata?.limit &&
                        ` (limit: ${price?.metadata?.limit})`}
                    </Text>
                    {isLicensedPrice(price) && isTieredPrice(price) && (
                      <Box ml={4} className="licensed-tiers">
                        <Text>{describeLicensedTiers(price, product.unit_label)}</Text>
                      </Box>
                    )}
                    {price.billing_scheme === "tiered" && !isLicensedPrice(price) &&
                      price.tiers.map((tier, tierIndex) => (
                        <Box key={tierIndex} ml={4}>
                          <Text>
                            {`Tier ${tierIndex + 1}: $${
                              tier.unit_amount / 100
                            } per ${product.unit_label} `}
                            {tier.up_to !== null
                              ? `between ${
                                  tierIndex === 0
                                    ? 0
                                    : price.tiers[tierIndex - 1]?.up_to + 1
                                } and ${tier.up_to} ${product.unit_label}`
                              : `Anything more or equal than ${
                                  tierIndex === 0
                                    ? 0
                                    : price.tiers[tierIndex - 1]?.up_to + 1
                                }`}
                            {price?.metadata?.limit &&
                              ` (limit: ${price?.metadata?.limit})`}
                          </Text>
                        </Box>
                      ))}
                  </Box>
                ))}
              </Box>
              <Box mt={4}>
                <Text fontWeight="bold">
                  Fixed Price: ${calculateFixedPrice(bundle)}
                </Text>
              </Box>
            </Box>
          </Box>
        ))}
      </SimpleGrid>
    ), // PERF‑ADDED
    [bundles] // PERF‑ADDED
  ); // PERF‑ADDED

  return (
    <Box
      p={4}
      overflowY="auto"
      maxHeight="100vh"
      width="100%"
      position="relative"
    >
      <Flex justifyContent="flex-end" alignItems="center" mb={4}>
        <Button leftIcon={<AddIcon />} onClick={onOpen}>
          Add Bundle
        </Button>
      </Flex>

      {bundlesGrid} {/* PERF‑ADDED */}

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Bundle</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl id="bundle-name" mb={4}>
              <FormLabel>Name</FormLabel>
              <Input
                value={newBundle.name}
                onChange={(e) =>
                  setNewBundle({ ...newBundle, name: e.target.value })
                }
                placeholder="Bundle Name"
              />
            </FormControl>
            <FormControl id="bundle-description" mb={4}>
              <FormLabel>Description</FormLabel>
              <Input
                value={newBundle.description}
                onChange={(e) =>
                  setNewBundle({ ...newBundle, description: e.target.value })
                }
                placeholder="Bundle Description"
              />
            </FormControl>
            {newBundle.items.map((item, index) => {
              const product = products.find((p) => p.id === item.product);
              return (
                <Flex key={index} mb={4} alignItems="center">
                  <FormControl flex="1" mr={2}>
                    <FormLabel>Product</FormLabel>
                    <Select
                      testid={`product-${index}`}
                      value={item.product}
                      onChange={(e) =>
                        handleProductChange(index, "product", e.target.value)
                      }
                      className="bundle-product"
                    >
                      <option value="">Select Product</option>
                      {products
                        .filter(
                          (p) =>
                            !newBundle.items.some((i) => i.product === p.id) ||
                            p.id === item.product
                        )
                        .map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                    </Select>
                  </FormControl>
                  <FormControl flex="1" ml={2}>
                    <FormLabel>Pricing</FormLabel>
                    <Select
                      testid={`pricing-${index}`}
                      value={item.price}
                      onChange={(e) =>
                        handleProductChange(index, "price", e.target.value)
                      }
                      isDisabled={!item.product}
                      className="bundle-pricing"
                    >
                      <option value="">Select Pricing</option>
                      {product &&
                        product.plans.map((plan) => (
                          <option key={plan.id} value={plan.id}>
                            {plan.nickname ? `${plan.nickname}-` : ""}
                            {plan.billing_scheme === "tiered"
                              ? `$${plan.tiers[0].unit_amount / 100} per ${
                                  product.unit_label
                                }`
                              : `$${plan.amount / 100} per ${
                                  product.unit_label
                                }`}
                            {plan.limit && ` - limit: ${plan.limit}`}
                          </option>
                        ))}
                    </Select>
                  </FormControl>
                  <IconButton
                    icon={<DeleteIcon />}
                    onClick={() => handleRemoveProduct(index)}
                    ml={2}
                    alignSelf="flex-end"
                  />
                </Flex>
              );
            })}
            <Button
              onClick={handleAddProduct}
              leftIcon={<AddIcon />}
              colorScheme="teal"
              mb={4}
            >
              Add Product
            </Button>

            <FormControl id="partner-select" mb={4}>
              <FormLabel>Partner</FormLabel>
              <Select
                testid={"partner-select"}
                placeholder="Select Partner"
                value={newBundle.partner_id}
                onChange={(e) =>
                  setNewBundle({
                    ...newBundle,
                    partner_id: e.target.value,
                    overage_fee_share: e.target.value
                      ? newBundle.overage_fee_share
                      : "", // ADDED ➜ reset when cleared
                  })
                }
              >
                {partners.map((partner) => (
                  <option
                    key={partner._id}
                    value={partner._id}
                    id={`partner-${partner.name.replace(/ /g, "-")}`}
                  >
                    {partner.name}
                  </option>
                ))}
              </Select>
            </FormControl>

            {newBundle.partner_id && ( // ADDED ➜ conditional field
              <FormControl id="overage-split" mb={4} isRequired>
                <FormLabel>Overage Split % for partner</FormLabel>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  placeholder="0 – 100"
                  value={newBundle.overage_fee_share}
                  onChange={(e) =>
                    setNewBundle({
                      ...newBundle,
                      overage_fee_share: e.target.value,
                    })
                  }
                />
              </FormControl>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose} mr={3}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSaveBundle}
              isDisabled={isSavingBundle}
            >
              {isSavingBundle ? <Spinner /> : "Save"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Bundles;
